import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import FormLayout from '@/components/layout/FormLayout';
import { PermissionGuard } from '@/components/layout';
import type { EmployeeProfile } from '@/api/domains/employee';
import {
  normalizeWatchValue,
  normalizeWatchValueToString,
} from '@/utils/formHelpers';


interface PersonalDataFormProps {
  profile: EmployeeProfile | null;
  genderOptions: { value: string; label: string }[];
  maritalStatusOptions: { value: string; label: string }[];
  employmentStatusOptions: { value: string; label: string }[];
  syncAssistance?: boolean;
  onCancel: () => void;
  personalForm: {
    register: any;
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    setValue: any;
    watch: any;
    errors: any;
    isSubmitting: boolean;
  };
}

export const EmployeeProfilePersonalDataForm: React.FC<PersonalDataFormProps> = ({
  profile,
  genderOptions,
  maritalStatusOptions,
  employmentStatusOptions,
  syncAssistance = false,
  onCancel,
  personalForm,
}) => {
  const { t: translate } = useTranslation();
  const { register, handleSubmit, setValue, watch, errors, isSubmitting } = personalForm;

  // Watch for controlled inputs
  const birthDate = watch('birthDate');
  const genderCode = watch('genderCode');
  const maritalStatusId = watch('maritalStatusId');
  const employmentStatus = watch('employmentStatus');

  if (!profile) return null;

  return (
    <FormLayout onSubmit={handleSubmit} columns={4}>
      <FormLayout.Section title="Datos personales del colaborador">
        {/* Row 1: Nombre de usuario, Rut, Fecha de nacimiento, Empleado no sincroniza */}
        <LabelInput
          label={translate('employee:fields.username')}
          value={profile.userName}
          disabled={true}
        />
        <LabelInput
          label="RUT"
          value={profile.rutFull}
          disabled={true}
        />
        <DatePicker
          label="Fecha de nacimiento"
          value={normalizeWatchValue(birthDate)}
          onChange={(value) => setValue('birthDate', normalizeWatchValueToString(value), { shouldDirty: true })}
          placeholder="dd/mm/aaaa"
          color={errors.birthDate ? 'error' : undefined}
          supportingText={errors.birthDate?.message}
        />
        {syncAssistance ? (
          <Checkbox
            label={translate('employee:fields.doesNotSync')}
            tooltipText={translate('employee:fields.doesNotSyncTooltip')}
            checked={!!watch('doesNotSync')}
            onChange={(checked) => setValue('doesNotSync', checked, { shouldDirty: true })}
            data-testid="empleado-no-sincroniza-checkbox"
          />
        ) : (
          <div aria-hidden="true" className="hidden md:block" />
        )}

        {/* Row 2: Nombre, Apellido paterno, Apellido materno */}
        <LabelInput 
          label="Nombres" 
          registration={register('firstNames')}
          color={errors.firstNames ? 'error' : undefined}
          supportingText={errors.firstNames?.message}
        />
        <LabelInput 
          label="Apellido paterno" 
          registration={register('lastName')}
          color={errors.lastName ? 'error' : undefined}
          supportingText={errors.lastName?.message}
        />
        <LabelInput 
          label="Apellido materno" 
          registration={register('secondLastName')}
          color={errors.secondLastName ? 'error' : undefined}
          supportingText={errors.secondLastName?.message}
        />

        {/* Row 3: Sexo, Estado civil, Estado perfil */}
        <Selector
          label="Sexo"
          value={genderCode || undefined}
          onChange={(value) => setValue('genderCode', value as string, { shouldDirty: true })}
          options={genderOptions}
          placeholder="Selecciona sexo"
          color={errors.genderCode ? 'error' : 'default'}
          supportingText={errors.genderCode?.message}
        />
        <Selector
          label="Estado civil"
          value={maritalStatusId ? String(maritalStatusId) : undefined}
          onChange={(value) => setValue('maritalStatusId', value ? Number(value) : 0, { shouldDirty: true })}
          options={maritalStatusOptions}
          placeholder="Selecciona estado civil"
          color={errors.maritalStatusId ? 'error' : 'default'}
          supportingText={errors.maritalStatusId?.message}
        />
        <Selector
          label={translate('employee:fields.statusProfile')}
          value={employmentStatus || undefined}
          onChange={(value) => setValue('employmentStatus', value as string, { shouldDirty: true })}
          options={employmentStatusOptions}
          placeholder={translate('employee:fields.statusProfilePlaceholder')}
          color={errors.employmentStatus ? 'error' : 'default'}
          supportingText={errors.employmentStatus?.message}
        />
      </FormLayout.Section>

      <FormLayout.Footer
        cancelButton={
          <PrimaryButton type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </PrimaryButton>
        }
        primaryButton={
          <PermissionGuard action="edit">
            <PrimaryButton type="submit" isLoading={isSubmitting}>
              Guardar
            </PrimaryButton>
          </PermissionGuard>
        }
      />
    </FormLayout>
  );
};

export default EmployeeProfilePersonalDataForm;
